import {
  call,
  CallEffect,
  put,
  PutEffect,
  takeLatest,
} from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import {
  AdminUserResponse,
  AuthMethodsResponse,
  CreateAdminRequest,
  GetUsersListResponse,
  UpdateAdminRequest,
} from '../api/types';
import { PayloadAction } from '@reduxjs/toolkit';
import { getArrayApiErrors } from 'utils/get-array-api-errors';
import {
  createAdminUser,
  deleteAdminUser,
  deleteAdminUsers,
  getAdminUser,
  getAuthMethods,
  getUsersList,
  updateAdminUser,
} from 'features/admin-users/api';
import { adminUsersActions } from 'features/admin-users/redux/admin-users.actions';
import { toast } from 'react-toastify';
import { extractErrorMessage } from 'utils/extract-error-message.util';

function* getAdminUsersList({
  payload,
}: PayloadAction<{
  page: number;
  limit: number;
  selectedRole?: number;
  search?: string;
}>): Generator<
  CallEffect<AxiosResponse<GetUsersListResponse>> | PutEffect,
  void,
  AxiosResponse<GetUsersListResponse>
> {
  try {
    const response = yield call(getUsersList, payload);
    yield put(adminUsersActions.getAdminUsersListSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      adminUsersActions.getAdminUsersListFailure(getArrayApiErrors(error))
    );
  }
}

function* createUser(
  data: PayloadAction<CreateAdminRequest>
): Generator<
  CallEffect<AxiosResponse<AdminUserResponse>> | PutEffect,
  void,
  AxiosResponse<AdminUserResponse>
> {
  try {
    const response = yield call(createAdminUser, data.payload);
    toast.success('User created successfully');
    yield put(adminUsersActions.createUserSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(adminUsersActions.createUserFailure(getArrayApiErrors(error)));
  }
}

function* getUser(
  params: PayloadAction<{ userId: number }>
): Generator<
  CallEffect<AxiosResponse<AdminUserResponse>> | PutEffect,
  void,
  AxiosResponse<AdminUserResponse>
> {
  try {
    const response = yield call(getAdminUser, params.payload);
    yield put(adminUsersActions.getUserSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(adminUsersActions.getUserFailure(getArrayApiErrors(error)));
  }
}

function* updateUser(
  data: PayloadAction<{
    data: UpdateAdminRequest;
    userId: number;
  }>
): Generator<
  CallEffect<AxiosResponse<AdminUserResponse>> | PutEffect,
  void,
  AxiosResponse<AdminUserResponse>
> {
  try {
    const response = yield call(updateAdminUser, data.payload);
    toast.success('User updated successfully');
    yield put(adminUsersActions.updateUserSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(adminUsersActions.updateUserFailure(getArrayApiErrors(error)));
  }
}

function* deleteUser({
  payload,
}: PayloadAction<{ userId: number }>): Generator<
  CallEffect<number> | PutEffect,
  void,
  number
> {
  try {
    const response = yield call(deleteAdminUser, { userId: payload.userId });
    toast.success('User deleted successfully');
    yield put(adminUsersActions.deleteUserSuccess(response));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(adminUsersActions.deleteUserFailure(getArrayApiErrors(error)));
  }
}

function* deleteUsers({
  payload,
}: PayloadAction<number[]>): Generator<
  CallEffect<number[]> | PutEffect,
  void,
  number[]
> {
  try {
    const response = yield call(deleteAdminUsers, payload);
    toast.success('Users deleted successfully');
    yield put(adminUsersActions.deleteUsersSuccess(response));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(adminUsersActions.deleteUsersFailure(getArrayApiErrors(error)));
  }
}

function* getAuthMethodsList({
  payload,
}: PayloadAction<{ partnerName: string }>): Generator<
  CallEffect<AxiosResponse<AuthMethodsResponse>> | PutEffect,
  void,
  AxiosResponse<AuthMethodsResponse>
> {
  try {
    const response = yield call(getAuthMethods, payload.partnerName);
    yield put(adminUsersActions.getAuthMethodsSuccess(response.data));
  } catch (error) {
    toast.error(extractErrorMessage(error));
    yield put(
      adminUsersActions.getAuthMethodsFailure(getArrayApiErrors(error))
    );
  }
}

function* getAdminUsersSaga() {
  yield takeLatest(
    adminUsersActions.getAdminUsersListRequest,
    getAdminUsersList
  );
  yield takeLatest(adminUsersActions.createUserRequest, createUser);
  yield takeLatest(adminUsersActions.getUserRequest, getUser);
  yield takeLatest(adminUsersActions.updateUserRequest, updateUser);
  yield takeLatest(adminUsersActions.deleteUserRequest, deleteUser);
  yield takeLatest(adminUsersActions.getAuthMethodsRequest, getAuthMethodsList);
  yield takeLatest(adminUsersActions.deleteUsersRequest, deleteUsers);
}

export default getAdminUsersSaga;
